import { nonEmptyString } from 'src/modules/shared/utils/formatting/non-empty-string.util';
import { format } from 'date-fns';
import { Fragment } from 'react';
import { PiUser } from 'react-icons/pi';
import { TCFunnelStatusChangedBySource } from 'src/api/generated';
import { useUser } from 'src/modules/shared/hooks/api/admin/users';
import { formatFullName } from 'src/modules/shared/utils/formatting/name.util';
import { Text } from 'src/modules/shared/components/atoms/text';
import { FunnelStatusName } from 'src/modules/shared/components/atoms/funnel-status-name';
import { AiRobotOutlineIcon } from 'src/modules/shared/components/atoms/indicators/ai-badge';
import Tag from 'src/modules/shared/components/atoms/tag';
import { getStatusVariant } from 'src/modules/shared/utils/status-color.util';
import { TimelineItemActions } from './timeline-item-actions';
import type { TimelineStatusItemProps } from '../../../types/timeline/compact-timeline.types';

const ACTORS = {
  [TCFunnelStatusChangedBySource.USER]: 'Usuario',
  [TCFunnelStatusChangedBySource.AI]: 'Asistente IA',
  [TCFunnelStatusChangedBySource.SYSTEM]: 'Automático',
};

/** Full transition labels stay in the event; no duplicate details dialog is needed. */
export function TimelineStatusItem({
  item,
  statuses = [],
}: TimelineStatusItemProps) {
  const userId = item.actor.userId ? Number(item.actor.userId) : null;
  const { user, loading } = useUser(userId ?? -1, userId !== null);
  const actor = loading
    ? 'Cargando...'
    : user && item.actor.type === TCFunnelStatusChangedBySource.USER
      ? formatFullName(user.firstName, user.lastName, user.email)
      : ACTORS[item.actor.type];
  const from = statuses.find((status) => status.id === item.fromStatusId);
  const to = statuses.find((status) => status.id === item.toStatusId);
  const fromLabel =
    nonEmptyString(from?.name) ??
    (item.fromStatusId ? `Estado ${item.fromStatusId}` : 'Sin estado previo');
  const toLabel =
    nonEmptyString(to?.name) ??
    (item.toStatusId ? `Estado ${item.toStatusId}` : 'Sin estado');
  const date = new Date(item.changedAt ?? item.createdAt);
  const isManual = item.actor.type === TCFunnelStatusChangedBySource.USER;
  return (
    <div className="mx-auto w-fit max-w-full py-1 text-secondary-600 [@media(hover:hover)_and_(pointer:fine)]:max-w-[calc(100%_-_5.5rem)]">
      <TimelineItemActions
        label={`Opciones del cambio de estado, ${format(date, 'HH:mm')}`}
        copyText={`${actor}: ${fromLabel} → ${toLabel} · ${format(date, "PP 'a las' HH:mm")}`}
      >
        <div className="flex min-w-0 flex-wrap items-center justify-center gap-x-1.5 gap-y-1">
          <span
            role="img"
            aria-label={ACTORS[item.actor.type]}
            className="shrink-0"
          >
            {isManual ? (
              <PiUser aria-hidden="true" className="h-4 w-4" />
            ) : (
              <AiRobotOutlineIcon className="h-5 w-5" />
            )}
          </span>
          {isManual && (
            <Text
              size="tiny"
              className="max-w-full break-words [overflow-wrap:anywhere]"
            >
              {actor}
            </Text>
          )}
          {[
            { key: 'from', status: from, label: fromLabel },
            { key: 'to', status: to, label: toLabel },
          ].map(({ key, status, label }, index) => (
            <Fragment key={key}>
              {index > 0 && (
                <Text as="span" size="tiny">
                  →
                </Text>
              )}
              <Tag
                size="sm"
                appearance="soft"
                variant={
                  status
                    ? getStatusVariant(status.name, status.terminalOutcome)
                    : 'neutral'
                }
                className="min-w-0 max-w-full"
                textClassName="min-w-0 !whitespace-normal"
                content={
                  <FunnelStatusName
                    name={label}
                    outcome={status?.terminalOutcome}
                    size="tiny"
                    className="max-w-full [&>span]:!whitespace-normal [&>span]:break-words [&>span]:[overflow-wrap:anywhere]"
                  />
                }
              />
            </Fragment>
          ))}
          <Text
            size="tiny"
            color="secondary"
            title={format(date, "PP 'a las' HH:mm")}
          >
            {format(date, 'HH:mm')}
          </Text>
        </div>
      </TimelineItemActions>
    </div>
  );
}
